// src/pages/TransactionalData.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader,
    AlertTriangle,
    RefreshCw,
    ExternalLink,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
} from 'lucide-react';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import Navbar from '../components/navbar';

const PRIMARY = '#326BDF';

const COLUMN_CATEGORY_MAP = {
    dpp_created_by: 'dpp_created_by',
    data_carrier_type: 'data_carrier_type',
    link_to_digital_copy: 'link_to_digital_copy',
    ownership: 'ownership',
    passport_availability_period: 'passport_availability_period',
    traceability_events: 'traceability_events',
    location_id: 'location_id',
};

const TRANSACTIONAL_KEYS = Object.keys(COLUMN_CATEGORY_MAP);

function formatTs(ts) {
    if (!ts) return 'N/A';
    try {
        return new Date(ts).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return ts; }
}

function TransactionalData() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [transactionalData, setTransactionalData] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [visibleCategories, setVisibleCategories] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(30);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 0, hasMore: false });

    const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

    const getUserRole = () => {
        try {
            const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
            if (!token) return 'Manufacturer';
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.type || payload.role || 'Manufacturer';
        } catch { return 'Manufacturer'; }
    };

    const userRole = getUserRole();
    const canLogEvent = ['Repair/Refurb', 'Recycler'].includes(userRole);

    const canSeeColumn = (fieldKey) => {
        if (userRole === 'Manufacturer') return true;
        if (!visibleCategories) return true;
        const categoryKey = COLUMN_CATEGORY_MAP[fieldKey] || fieldKey;
        return visibleCategories.includes(categoryKey);
    };

    // ── RBAC fetch ──
    useEffect(() => {
        if (userRole === 'Manufacturer') return;
        const fetchRoleAccess = async () => {
            try {
                const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/role-access`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-User-Role': userRole,
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = await response.json();
                if (data.success && data.data[userRole]) {
                    const roleData = data.data[userRole];
                    const visible = Object.entries(roleData)
                        .filter(([k, v]) => v.isVisible && TRANSACTIONAL_KEYS.includes(k))
                        .map(([k]) => k);
                    setVisibleCategories(visible);
                } else {
                    setVisibleCategories([]);
                }
            } catch (err) {
                console.error('Failed to fetch role access:', err);
                setVisibleCategories(null);
            }
        };
        fetchRoleAccess();
    }, []);

    const fetchTransactionalData = async (page = 1) => {
        let spinnerTimer = null;
        try {
            setLoading(true);
            setError(null);
            spinnerTimer = setTimeout(() => setShowSpinner(true), 900);
            const offset = (page - 1) * itemsPerPage;
            const response = await fetch(`${API_BASE_URL}/getallproducts?limit=${itemsPerPage}&offset=${offset}`, {
                method: 'GET', headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) throw new Error(`Failed to fetch data: ${response.status}`);
            const data = await response.json();
            let dataList = [], paginationData = null;
            if (data.success && data.data?.records && Array.isArray(data.data.records)) {
                dataList = data.data.records; paginationData = data.data.pagination;
            } else if (data.records && Array.isArray(data.records)) {
                dataList = data.records; paginationData = data.pagination;
            } else if (Array.isArray(data)) {
                dataList = data;
            }
            setTransactionalData(dataList);
            setPagination(paginationData
                ? { total: paginationData.total || dataList.length, totalPages: paginationData.totalPages || 1, hasMore: paginationData.hasMore || false }
                : { total: dataList.length, totalPages: 1, hasMore: false }
            );
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            let msg = err.message;
            if (msg.includes('Failed to fetch') || msg.includes('Network')) msg = 'Network error. Please check your connection.';
            setError(msg);
        } finally {
            if (spinnerTimer) clearTimeout(spinnerTimer);
            setShowSpinner(false);
            setLoading(false);
        }
    };

    useEffect(() => { fetchTransactionalData(currentPage); }, [currentPage]);

    const handlePreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
    const handleNextPage = () => { if (currentPage < pagination.totalPages) setCurrentPage(currentPage + 1); };
    const handleRetry = () => { setError(null); setCurrentPage(1); fetchTransactionalData(1); };
    const getShowingRange = () => ({ start: (currentPage - 1) * itemsPerPage + 1, end: Math.min(currentPage * itemsPerPage, pagination.total) });
    const getDigitalCopyLink = (item) => `/product-passport/${item.id}`;

    // ── Log Event Button ──
    const LogEventButton = ({ item, size = 'md' }) => {
        const isSmall = size === 'sm';
        return (
            <button
                onClick={() => navigate(`/lifecycle-event/${item.id}`)}
                className={`inline-flex items-center gap-${isSmall ? '1' : '2'} px-${isSmall ? '2' : '3'} py-1.5 ${isSmall ? 'text-xs' : 'text-sm'} rounded-lg transition-colors whitespace-nowrap`}
                style={{
                    backgroundColor: '#EEF3FF',
                    color: PRIMARY,
                    border: `1px solid #C7D7F9`,
                    cursor: 'pointer',
                }}
            >
                <ClipboardList className={`w-${isSmall ? '3' : '4'} h-${isSmall ? '3' : '4'}`} />
                Log Event
            </button>
        );
    };

    const PaginationControls = () => {
        const { start, end } = getShowingRange();
        return (
            <div className="bg-white border-t border-gray-200 px-4 py-3 sm:px-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-sm text-gray-700">
                        Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of{' '}
                        <span className="font-medium">{pagination.total}</span> records
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handlePreviousPage} disabled={currentPage === 1}
                            className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                            <ChevronLeft className="w-4 h-4" />Previous
                        </button>
                        <div className="px-3 py-1.5 text-sm font-medium text-gray-700">Page {currentPage} of {pagination.totalPages}</div>
                        <button onClick={handleNextPage} disabled={currentPage === pagination.totalPages}
                            className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${currentPage === pagination.totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                            Next<ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Mobile Card ──
    const DataCard = ({ item }) => {
        const lastEvent = Array.isArray(item.traceability_events) && item.traceability_events.length > 0
            ? item.traceability_events[item.traceability_events.length - 1] : null;
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                <div className="space-y-3">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5">DPP ID</p>
                            <p className="text-sm font-semibold text-gray-900 break-all">{item.id || 'N/A'}</p>
                        </div>
                        {item.id && <div className="ml-3"><QRCode value={item.id} size={60} level="M" /></div>}
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-0.5">Product ID</p>
                        <span className="text-sm text-gray-700">{item.product_id || 'N/A'}</span>
                    </div>
                    {canSeeColumn('dpp_created_by') && (
                        <div><p className="text-xs text-gray-500 mb-0.5">Created By</p>
                            <span className="text-sm text-gray-700">{item.dpp_created_by || 'N/A'}</span></div>
                    )}
                    {canSeeColumn('data_carrier_type') && (
                        <div><p className="text-xs text-gray-500 mb-0.5">Data Carrier</p>
                            <span className="text-sm text-gray-700">{item.data_carrier_type || 'N/A'}</span></div>
                    )}
                    {canSeeColumn('ownership') && (
                        <div><p className="text-xs text-gray-500 mb-0.5">Ownership</p>
                            <span className="text-sm text-gray-700">{item.ownership || 'N/A'}</span></div>
                    )}
                    {canSeeColumn('passport_availability_period') && (
                        <div><p className="text-xs text-gray-500 mb-0.5">Availability Period</p>
                            <span className="text-sm text-gray-700">{item.passport_availability_period || 'N/A'}</span></div>
                    )}
                    {canSeeColumn('location_id') && (
                        <div><p className="text-xs text-gray-500 mb-0.5">Current Location</p>
                            <span className="text-sm text-gray-700">{item.location_id || 'N/A'}</span></div>
                    )}
                    {canSeeColumn('traceability_events') && lastEvent && (
                        <div><p className="text-xs text-gray-500 mb-0.5">Last Known Location</p>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium text-gray-900">{lastEvent.location_id}</span>
                                <span className="text-xs text-gray-500">{formatTs(lastEvent.timestamp)}</span>
                                {lastEvent.currentUser && <span className="text-xs text-gray-400">By: {lastEvent.currentUser}</span>}
                            </div>
                        </div>
                    )}
                    <div className={`pt-2 border-t border-gray-200 flex gap-2 ${canLogEvent ? 'flex-col' : ''}`}>
                        {canSeeColumn('link_to_digital_copy') && (
                            <button onClick={() => navigate(getDigitalCopyLink(item))}
                                className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                                <ExternalLink className="w-4 h-4" />View Product Passport
                            </button>
                        )}
                        {canLogEvent && (
                            <button
                                onClick={() => navigate(`/lifecycle-event/${item.id}`)}
                                className="w-full px-3 py-2 text-sm rounded-lg flex items-center justify-center gap-2 transition-colors"
                                style={{
                                    backgroundColor: '#EEF3FF',
                                    color: PRIMARY,
                                    border: `1px solid #C7D7F9`,
                                    cursor: 'pointer',
                                }}
                            >
                                <ClipboardList className="w-4 h-4" />
                                Log Event
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (showSpinner && currentPage === 1) return (
        <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
            <Navbar />
            <div className="flex items-center justify-center h-[60vh]">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading transactional data...</p>
                </div>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
            <Navbar />
            <div className="flex items-center justify-center h-[60vh]">
                <div className="text-center max-w-md mx-auto p-6">
                    <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button onClick={handleRetry} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto">
                        <RefreshCw className="w-4 h-4" />Retry
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
            <Navbar />
            {showSpinner && currentPage > 1 && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 flex items-center gap-3">
                        <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                        <p className="text-gray-700">Loading page {currentPage}...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl overflow-hidden shadow">
                <button onClick={() => navigate('/dashboard')} className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base">
                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />Back
                </button>
                <h2 className="text-blue-600 font-medium text-sm sm:text-base">
                    Transactional Data ({pagination.total} records)
                </h2>
            </div>

            <div className="p-4 sm:p-6 lg:p-8">

                {/* ── Desktop Table ── */}
                <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead style={{ backgroundColor: '#D6E1F9' }}>
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">DPP ID</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Product ID</th>
                                    {canSeeColumn('link_to_digital_copy') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Product Passport</th>}
                                    {canLogEvent && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
                                    {canSeeColumn('dpp_created_by') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Created By</th>}
                                    {canSeeColumn('data_carrier_type') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Data Carrier</th>}
                                    {canSeeColumn('ownership') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Ownership</th>}
                                    {canSeeColumn('passport_availability_period') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Availability Period</th>}
                                    {canSeeColumn('location_id') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Current Location</th>}
                                    {canSeeColumn('traceability_events') && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Last Known Location</th>}
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">QR Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactionalData.map((item, index) => {
                                    const lastEvent = Array.isArray(item.traceability_events) && item.traceability_events.length > 0
                                        ? item.traceability_events[item.traceability_events.length - 1] : null;
                                    return (
                                        <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{item.id || 'N/A'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{item.product_id || 'N/A'}</td>
                                            {canSeeColumn('link_to_digital_copy') && (
                                                <td className="px-4 py-4">
                                                    <button onClick={() => window.open(getDigitalCopyLink(item), '_blank', 'noopener,noreferrer')}
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
                                                        <ExternalLink className="w-4 h-4" />View Passport
                                                    </button>
                                                </td>
                                            )}
                                            {canLogEvent && (
                                                <td className="px-4 py-4">
                                                    <LogEventButton item={item} size="md" />
                                                </td>
                                            )}
                                            {canSeeColumn('dpp_created_by') && <td className="px-4 py-4 text-sm text-gray-700">{item.dpp_created_by || 'N/A'}</td>}
                                            {canSeeColumn('data_carrier_type') && <td className="px-4 py-4 text-sm text-gray-700">{item.data_carrier_type || 'N/A'}</td>}
                                            {canSeeColumn('ownership') && <td className="px-4 py-4 text-sm text-gray-700">{item.ownership || 'N/A'}</td>}
                                            {canSeeColumn('passport_availability_period') && <td className="px-4 py-4 text-sm text-gray-700">{item.passport_availability_period || 'N/A'}</td>}
                                            {canSeeColumn('location_id') && <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{item.location_id || 'N/A'}</td>}
                                            {canSeeColumn('traceability_events') && (
                                                <td className="px-4 py-4 text-sm text-gray-700">
                                                    {lastEvent ? (
                                                        <div className="flex flex-col gap-0.5">
                                                            <span className="font-medium text-gray-900">{lastEvent.location_id || '—'}</span>
                                                            <span className="text-xs text-gray-500">{formatTs(lastEvent.timestamp)}</span>
                                                            {lastEvent.currentUser && <span className="text-xs text-gray-400">By: {lastEvent.currentUser}</span>}
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                            )}
                                            <td className="px-4 py-4 text-center">
                                                {item.id ? <div className="inline-block"><QRCode value={item.id} size={80} level="M" /></div> : <span className="text-sm text-gray-400">No QR</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {pagination.totalPages > 1 && <PaginationControls />}
                </div>

                {/* ── Tablet Table ── */}
                <div className="hidden md:block lg:hidden bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px]">
                            <thead style={{ backgroundColor: '#D6E1F9' }}>
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">DPP ID</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Product ID</th>
                                    {canLogEvent && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Action</th>}
                                    {canSeeColumn('dpp_created_by') && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Created By</th>}
                                    {canSeeColumn('data_carrier_type') && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Data Carrier</th>}
                                    {canSeeColumn('ownership') && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Ownership</th>}
                                    {canSeeColumn('passport_availability_period') && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Availability</th>}
                                    {canSeeColumn('location_id') && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Location</th>}
                                    {canSeeColumn('traceability_events') && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Last Known Location</th>}
                                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">QR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactionalData.map((item, index) => {
                                    const lastEvent = Array.isArray(item.traceability_events) && item.traceability_events.length > 0
                                        ? item.traceability_events[item.traceability_events.length - 1] : null;
                                    return (
                                        <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-3 py-3 text-xs font-medium text-gray-900 whitespace-nowrap">{item.id || 'N/A'}</td>
                                            <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{item.product_id || 'N/A'}</td>
                                            {canLogEvent && (
                                                <td className="px-3 py-3">
                                                    <LogEventButton item={item} size="sm" />
                                                </td>
                                            )}
                                            {canSeeColumn('dpp_created_by') && <td className="px-3 py-3 text-xs text-gray-700">{item.dpp_created_by || 'N/A'}</td>}
                                            {canSeeColumn('data_carrier_type') && <td className="px-3 py-3 text-xs text-gray-700">{item.data_carrier_type || 'N/A'}</td>}
                                            {canSeeColumn('ownership') && <td className="px-3 py-3 text-xs text-gray-700">{item.ownership || 'N/A'}</td>}
                                            {canSeeColumn('passport_availability_period') && <td className="px-3 py-3 text-xs text-gray-700">{item.passport_availability_period || 'N/A'}</td>}
                                            {canSeeColumn('location_id') && <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{item.location_id || 'N/A'}</td>}
                                            {canSeeColumn('traceability_events') && (
                                                <td className="px-3 py-3 text-xs text-gray-700">
                                                    {lastEvent ? (
                                                        <div className="flex flex-col gap-0.5">
                                                            <span className="font-medium text-gray-900">{lastEvent.location_id || '—'}</span>
                                                            <span className="text-gray-500">{formatTs(lastEvent.timestamp)}</span>
                                                            {lastEvent.currentUser && <span className="text-gray-400">By: {lastEvent.currentUser}</span>}
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                            )}
                                            <td className="px-3 py-3 text-center">
                                                {item.id ? <div className="inline-block"><QRCode value={item.id} size={60} level="M" /></div> : <span className="text-xs text-gray-400">No QR</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 text-center border-t">Scroll horizontally to see more columns →</div>
                    {pagination.totalPages > 1 && <PaginationControls />}
                </div>

                {/* ── Mobile Cards ── */}
                <div className="md:hidden">
                    {transactionalData.map((item, index) => <DataCard key={item.id || index} item={item} />)}
                    {pagination.totalPages > 1 && <div className="bg-white rounded-lg shadow-sm mt-4"><PaginationControls /></div>}
                </div>

                {/* ── Empty State ── */}
                {transactionalData.length === 0 && !loading && (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                        <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No transactional data found</h3>
                        <p className="text-gray-600">There are currently no records in the system.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TransactionalData;